import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { MdSupportAgent } from "react-icons/md";
import CustomDropdown from './CustomDropdown';
import { contactSupportService } from "../../services/api";
import toast from "react-hot-toast";

const ContactSupportModal = ({ isOpen, onClose, onSuccess, prefaceMessage = "" }) => {
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState("normal");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [description, setDescription] = useState("");
  const [isPreferWhatsapp, setIsPreferWhatsapp] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isInputEditable, setIsInputEditable] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
      setIsInputEditable(false);
    }
  }, [user]);

  const priorityOptions = [
    { value: "low", label: "Low", color: "black" },
    { value: "normal", label: "Medium", color: "orange" },
    { value: "high", label: "High", color: "red" },
    { value: "urgent", label: "Urgent", color: "red" },
  ];

  const categoryOptions = [
    { value: "technical_problem", label: "Technical Problem" },
    { value: "feedback", label: "Feedback / Suggestion" },
    { value: "feature_request", label: "Request a Feature" },
    { value: "bug_report", label: "Report a Bug" },
  ];

  const FlagIcon = ({ color }) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 14C1 14 2 13 5 13C8 13 10 15 13 15C16 15 17 14 17 14V2C17 2 16 3 13 3C10 3 8 1 5 1C2 1 1 2 1 2V14ZM1 14V21"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const handlePrioritySelect = (priority) => {
    setSelectedPriority(priority);
    setShowPriorityDropdown(false);
  };

  const handleEditEmail = () => {
    setIsInputEditable(true);
    setUserEmail("");
    // Use setTimeout to ensure the input is enabled before focusing
    setTimeout(() => {
      emailInputRef.current?.focus();
    }, 0);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    if (!description.trim()) {
      toast.error("Please provide a description");
      return;
    }
    if (!userEmail.trim()) {
      toast.error("Please provide your email");
      return;
    }
    if (isPreferWhatsapp && !phoneNumber.trim()) {
      toast.error("Please provide your phone number for WhatsApp");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        category: selectedCategory,
        description: description.trim(),
        priority: selectedPriority,
        contact_email: userEmail.trim(),
        prefer_whatsapp: isPreferWhatsapp,
      };

      if (isPreferWhatsapp && phoneNumber.trim()) {
        payload.phone_number = phoneNumber.trim();
      }

      if (attachment) {
        payload.attachment = attachment;
      }

      const result = await contactSupportService.createSupportRequest(payload);

      if (result.success) {
        toast.success("Support request submitted successfully!");
        onClose();
        onSuccess();
        // Reset form
        setSelectedCategory("");
        setDescription("");
        setSelectedPriority("normal");
        setPhoneNumber("");
        setAttachment(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        toast.error(result.error || "Failed to submit support request");
      }
    } catch (error) {
      console.error("Error submitting support request:", error);
      toast.error("An error occurred while submitting your request");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
  <div onClick={onclose} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#0A0F2D4D] backdrop-blur-md transition-opacity duration-300 ease-out">
      <div
        onClick={(e) => e.stopPropagation()}
       className="w-full sm:w-[500px] max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl p-8 transform transition-all duration-300 ease-out motion-safe:animate-[slideUp_0.3s_ease-out_forwards]"
      >
        <div className="w-full text-center text-[#1A212F] text-xl font-bold">Contact Support </div>

        {prefaceMessage && (
          <div className="mt-3 w-full text-center text-[#1A212F] text-base">
            {prefaceMessage}
          </div>
        )}

        <div className="w-full flex flex-col gap-2 mt-5">
          <CustomDropdown
            label="Select Category"
            labelClassName="text-black text-[16px] font-medium"
            options={categoryOptions}
            value={selectedCategory}
            onChange={(value) => setSelectedCategory(value)}
            placeholder="Select a category"
            className="w-full h-[40px] border border-[#ECEEF6] rounded-lg text-base font-medium p-2 text-[16px]"
          />
        </div>

        <div className="flex w-full flex-col gap-2 mt-5">
          <label className="text-black text-[16px] font-medium">
            <span className="text-red-500">*</span> Description
          </label>

          <div className="w-full h-[150px] relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="relative resize-none w-full h-full border border-[#ECEEF6] rounded-xl p-2 text-[16px] placeholder:text-[#505050]"
              placeholder="Describe your issue"
            />
            <div 
              className="absolute bottom-5 left-5 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg
                width="10"
                height="18"
                viewBox="0 0 10 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.58333 17.3327C3.30556 17.3327 2.22222 16.8882 1.33333 15.9993C0.444444 15.1105 0 14.0271 0 12.7493V3.99935C0 3.08268 0.326389 2.29796 0.979167 1.64518C1.63194 0.992405 2.41667 0.666016 3.33333 0.666016C4.25 0.666016 5.03472 0.992405 5.6875 1.64518C6.34028 2.29796 6.66667 3.08268 6.66667 3.99935V11.916C6.66667 12.4993 6.46528 12.9924 6.0625 13.3952C5.65972 13.798 5.16667 13.9993 4.58333 13.9993C4 13.9993 3.50694 13.798 3.10417 13.3952C2.70139 12.9924 2.5 12.4993 2.5 11.916V3.99935H3.75V11.916C3.75 12.1521 3.82986 12.35 3.98958 12.5098C4.14931 12.6695 4.34722 12.7493 4.58333 12.7493C4.81944 12.7493 5.01736 12.6695 5.17708 12.5098C5.33681 12.35 5.41667 12.1521 5.41667 11.916V3.99935C5.41667 3.41602 5.21528 2.92296 4.8125 2.52018C4.40972 2.1174 3.91667 1.91602 3.33333 1.91602C2.75 1.91602 2.25694 2.1174 1.85417 2.52018C1.45139 2.92296 1.25 3.41602 1.25 3.99935V12.7493C1.25 13.666 1.57639 14.4507 2.22917 15.1035C2.88194 15.7563 3.66667 16.0827 4.58333 16.0827C5.5 16.0827 6.28472 15.7563 6.9375 15.1035C7.59028 14.4507 7.91667 13.666 7.91667 12.7493V3.99935H9.16667V12.7493C9.16667 14.0271 8.72222 15.1105 7.83333 15.9993C6.94445 16.8882 5.86111 17.3327 4.58333 17.3327Z"
                  fill="#1D1B20"
                />
              </svg>
            </div>
            {attachment && (
              <div className="absolute bottom-5 right-5 text-sm text-green-600 bg-white px-2 py-1 rounded">
                📎 {attachment.name}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
          </div>
        </div>

        <div className="w-full flex flex-col gap-2 mt-5">
          <CustomDropdown
            label="Select Priority"
            labelClassName="text-black text-[16px] font-medium"
            options={priorityOptions}
            value={selectedPriority}
            onChange={(value) => setSelectedPriority(value)}
            displayProperty="label"
            valueProperty="value"
            className="w-full h-[40px] border border-[#ECEEF6] rounded-lg"
          />
        </div>

        <div className="w-full flex flex-col gap-2 mt-5">
          <label className="text-black text-[16px] font-medium">
            Contact Email
          </label>
          <div className="relative w-full h-[40px] border border-[#ECEEF6] rounded-lg text-base font-medium p-2 text-[16px] placeholder:text-[#505050]">
            <input
              ref={emailInputRef}
              type="email"
              onChange={(e) => setUserEmail(e.target.value)}
              value={userEmail}
              disabled={!isInputEditable}
              className="w-full h-full outline-none"
              placeholder="Enter your email"
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 right-5 cursor-pointer"
              onClick={handleEditEmail}
            >
              <svg
                width="20"
                height="19"
                viewBox="0 0 20 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.166 2.0009C14.3849 1.78203 14.6447 1.60842 14.9307 1.48996C15.2167 1.37151 15.5232 1.31055 15.8327 1.31055C16.1422 1.31055 16.4487 1.37151 16.7347 1.48996C17.0206 1.60842 17.2805 1.78203 17.4993 2.0009C17.7182 2.21977 17.8918 2.47961 18.0103 2.76558C18.1287 3.05154 18.1897 3.35804 18.1897 3.66757C18.1897 3.9771 18.1287 4.2836 18.0103 4.56956C17.8918 4.85553 17.7182 5.11537 17.4993 5.33424L6.24935 16.5842L1.66602 17.8342L2.91602 13.2509L14.166 2.0009Z"
                  stroke="#626262"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col gap-2 mt-5">
          <div className="flex items-center gap-2">
            <label className="text-black text-[16px] font-medium">
              Prefer WhatsApp?
            </label>
            <button
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isPreferWhatsapp ? "bg-blue-600" : "bg-gray-200"
              }`}
              onClick={() => setIsPreferWhatsapp(!isPreferWhatsapp)}
            >
              <span
                className={`${
                  isPreferWhatsapp ? "translate-x-6" : "translate-x-1"
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out shadow-lg`}
              />
            </button>
          </div>

          {isPreferWhatsapp && (
            <div className="w-full h-[40px] border border-[#ECEEF6] rounded-lg text-base font-medium p-2 text-[16px] placeholder:text-[#505050]">
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full h-full outline-none"
                placeholder="Enter your WhatsApp number"
              />
            </div>
          )}
        </div>

        <div className="flex w-full justify-between mt-5">
          <div
            onClick={onClose}
            className="cursor-pointer w-[48%] bg-white border border-[#E6E6E6] rounded-lg p-2 text-center text-[14px] text-[#1E1E1E]"
          >
            Cancel
          </div>
          <div
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`cursor-pointer w-[48%] rounded-lg p-2 text-center text-[14px] ${
              isSubmitting 
                ? "bg-gray-400 text-gray-600 cursor-not-allowed" 
                : "bg-[#2283FF] text-white"
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSupportModal; 